import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_RECORDING_MS = 120_000;

export function useVoiceRecorder(onResult: (blob: Blob) => void) {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const autoStopTimeoutRef = useRef<number | null>(null);

  const supported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
      });
      streamRef.current = stream;
      // Prefer webm/opus, fall back to whatever the browser supports
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        setIsRecording(false);
        if (blob.size > 0) onResultRef.current(blob);
      };
      mediaRecorderRef.current = recorder;
      setError(null);
      recorder.start(250);
      setIsRecording(true);
      autoStopTimeoutRef.current = window.setTimeout(() => stop(), MAX_RECORDING_MS);
    } catch (err) {
      console.error('[voice-recorder] getUserMedia failed:', err);
      setError(err instanceof Error ? err.message : 'Microphone access denied');
    }
  }, []);

  const stop = useCallback(() => {
    if (autoStopTimeoutRef.current !== null) {
      clearTimeout(autoStopTimeoutRef.current);
      autoStopTimeoutRef.current = null;
    }
    // Set state immediately — do NOT rely solely on the onstop callback.
    // Same pattern as useSpeechToText: Android can fail to fire onstop,
    // leaving the button stuck yellow permanently.
    setIsRecording(false);
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.requestData();
    }
    try { recorder?.stop(); } catch { /* already stopped/inactive */ }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  const toggle = useCallback(() => {
    if (isRecording) stop();
    else start();
  }, [isRecording, start, stop]);

  useEffect(() => {
    return () => {
      if (autoStopTimeoutRef.current !== null) clearTimeout(autoStopTimeoutRef.current);
      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state === 'recording') recorder.requestData();
      recorder?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return { isRecording, toggle, start, stop, supported, error };
}
